import { Pencil, Trash2 } from "lucide-react";

export const ActiveBadge = ({ active }) => (
  <span
    className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${
      active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"
    }`}
  >
    {active ? "Active" : "Inactive"}
  </span>
);

/**
 * Shared compact table used by every System Value list (Services, Specialization,
 * Units, Currency, Language Pairs, ...). Tables for these screens always have
 * the same Status + Actions trailing columns; the caller only provides the
 * data columns via `columns`.
 *
 * @param {Object[]} items records to render
 * @param {Object[]} columns [{ header, render(item) }]
 * @param {(item)=>void} onEdit row edit handler
 * @param {(id)=>void} onDelete row delete handler (id only)
 * @param {string} [emptyMessage] shown when items is empty
 * @param {boolean} [showStatus=true] render Status column from item.active_flag
 * @param {boolean} [showActions=true] render the edit / delete column
 * @param {string|number} [minWidth=500] table min-width in pixels
 */
const SystemValueList = ({
  items = [],
  columns = [],
  onEdit,
  onDelete,
  emptyMessage = "No items added yet.",
  showStatus = true,
  showActions = true,
  minWidth = 500,
}) => {
  const headers = [
    ...columns.map((c) => c.header),
    ...(showStatus ? ["Status"] : []),
    ...(showActions ? ["Actions"] : []),
  ];

  return (
    <div className="w-full bg-white shadow rounded-lg overflow-hidden">
      <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
        <table
          className="w-full table-auto border-collapse"
          style={{ minWidth: typeof minWidth === "number" ? `${minWidth}px` : minWidth }}
        >
          <thead className="sticky top-0 z-10">
            <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
              {headers.map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={headers.length}
                  className="py-10 text-center text-gray-400 text-xs"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              items.map((item, index) => {
                const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                return (
                  <tr
                    key={item.id ?? index}
                    className={`${rowBg} hover:bg-brand-50 transition-colors`}
                  >
                    {columns.map((col, ci) => (
                      <td
                        key={ci}
                        className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-900"
                      >
                        {col.render(item)}
                      </td>
                    ))}
                    {showStatus && (
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                        <ActiveBadge active={item.active_flag} />
                      </td>
                    )}
                    {showActions && (
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <div className="flex justify-center gap-3">
                          <Pencil
                            className="w-4 h-4 text-brand-400 hover:text-brand-600 cursor-pointer transition-colors"
                            title="Edit"
                            onClick={() => onEdit?.(item)}
                          />
                          <Trash2
                            className="w-4 h-4 text-danger-400 hover:text-danger-600 cursor-pointer transition-colors"
                            title="Delete"
                            onClick={() => onDelete?.(item.id)}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SystemValueList;